/**
 * GA4 / GTM con Consent Mode v2.
 * Si hay VITE_GA_MEASUREMENT_ID, usa GA4 directo (más fiable).
 * GTM solo si no hay measurement ID.
 */

import { hasConsent } from '@/lib/cookieConsent';

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

const GTM_SCRIPT_ATTR = 'data-it-gtm';
const GTAG_SCRIPT_ATTR = 'data-it-gtag';

let tagsLoaded = false;
let analyticsConsentGranted = false;
let gtagJsReady = false;
let gtmReady = false;
let analyticsHitSent = false;

function dataLayer(): unknown[] {
  if (typeof window === 'undefined') return [];
  window.dataLayer = window.dataLayer ?? [];
  return window.dataLayer;
}

/** Replica el snippet oficial: dataLayer.push(arguments), no arrays. */
function gtagImpl(): void {
  dataLayer().push(arguments);
}

function gtag(...args: unknown[]): void {
  Reflect.apply(gtagImpl, undefined, args);
}

function trimEnv(value: string | undefined): string {
  return value?.trim() ?? '';
}

export function isGoogleTagsConfigured(): boolean {
  return Boolean(trimEnv(import.meta.env.VITE_GTM_ID) || trimEnv(import.meta.env.VITE_GA_MEASUREMENT_ID));
}

function googleAdsSendTo(labelEnv: string | undefined): string | null {
  const id = trimEnv(import.meta.env.VITE_GOOGLE_ADS_ID);
  const label = trimEnv(labelEnv);
  if (!id || !label) return null;
  return `${id}/${label}`;
}

function googleAdsDemoSendTo(): string | null {
  return googleAdsSendTo(import.meta.env.VITE_GOOGLE_ADS_CONVERSION_LABEL);
}

/** Conversión distinta a la demo: clic a WhatsApp de ventas. */
function googleAdsWhatsAppSendTo(): string | null {
  return googleAdsSendTo(import.meta.env.VITE_GOOGLE_ADS_WHATSAPP_CONVERSION_LABEL);
}

/**
 * Dispara conversión Google Ads con send_to AW-…/label.
 * No pasa por sendGa4Event: ese helper fuerza send_to=GA4 y rompe la etiqueta Ads.
 */
function sendGoogleAdsConversion(
  sendTo: string,
  options: { dataLayerEvent?: string; retries?: number[] } = {},
): void {
  const fire = () => {
    const send = window.gtag ?? gtag;
    send('event', 'conversion', { send_to: sendTo });
    if (options.dataLayerEvent) {
      pushDataLayerEvent(options.dataLayerEvent, { send_to: sendTo });
    }
    return gtagJsReady || gtmReady;
  };

  fire();
  for (const delay of options.retries ?? [150, 500, 1000, 2000, 3000, 5000]) {
    setTimeout(() => {
      ensureGoogleTagsLoaded();
      fire();
    }, delay);
  }
}

/** Debe ejecutarse antes de cargar gtag/GTM (también en index.html). */
export function initGoogleConsentDefaults(): void {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer ?? [];
  window.gtag = window.gtag ?? gtag;
  gtag('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'denied',
    functionality_storage: 'denied',
    personalization_storage: 'denied',
    security_storage: 'granted',
    wait_for_update: 2000,
  });
  gtag('set', 'ads_data_redaction', true);
}

function pushDataLayerEvent(event: string, params: Record<string, unknown> = {}): void {
  dataLayer().push({ event, ...params });
}

/** Alinea el estado interno con el consentimiento guardado en cookies. */
function ensureAnalyticsReady(): boolean {
  if (!isGoogleTagsConfigured() || !hasConsent('analytics')) return false;
  if (!analyticsConsentGranted) {
    syncGoogleConsent(true);
  } else {
    ensureGoogleTagsLoaded();
  }
  return true;
}

function ensureGa4Configured(send: (...args: unknown[]) => void, measurementId: string): void {
  if (!analyticsHitSent) {
    send('config', measurementId, { send_page_view: false });
    analyticsHitSent = true;
  }
}

/** Envía un evento GA4 solo cuando gtag.js está listo y con send_to explícito. */
function sendGa4Event(
  eventName: string,
  params: Record<string, unknown> = {},
  options: { dataLayerEvent?: string; retries?: number[] } = {},
): boolean {
  const measurementId = trimEnv(import.meta.env.VITE_GA_MEASUREMENT_ID);
  if (!measurementId || !gtagJsReady) return false;

  const send = window.gtag ?? gtag;
  ensureGa4Configured(send, measurementId);
  send('event', eventName, { ...params, send_to: measurementId });
  if (options.dataLayerEvent) {
    pushDataLayerEvent(options.dataLayerEvent, params);
  }
  return true;
}

function sendGtagEvent(
  eventName: string,
  params: Record<string, unknown> = {},
  options: { dataLayerEvent?: string; retries?: number[] } = {},
): void {
  if (sendGa4Event(eventName, params, options)) return;

  const gtmId = trimEnv(import.meta.env.VITE_GTM_ID);
  const fire = () => {
    if (trimEnv(import.meta.env.VITE_GA_MEASUREMENT_ID)) {
      return sendGa4Event(eventName, params, options);
    }
    if (!gtmId || !gtmReady) return false;
    const send = window.gtag ?? gtag;
    send('event', eventName, params);
    if (options.dataLayerEvent) {
      pushDataLayerEvent(options.dataLayerEvent, params);
    }
    return true;
  };

  fire();
  for (const delay of options.retries ?? [150, 500, 1000, 2000, 3000, 5000, 8000, 12000]) {
    setTimeout(() => {
      ensureGoogleTagsLoaded();
      fire();
    }, delay);
  }
}

function sendGa4PageView(): void {
  if (!analyticsConsentGranted) return;

  const measurementId = trimEnv(import.meta.env.VITE_GA_MEASUREMENT_ID);
  const pagePath = window.location.pathname || '/';

  if (measurementId && gtagJsReady) {
    const send = window.gtag ?? gtag;
    send('config', measurementId, { page_path: pagePath, send_page_view: true });
    send('event', 'page_view', { page_path: pagePath });
    analyticsHitSent = true;
    return;
  }

  const gtmId = trimEnv(import.meta.env.VITE_GTM_ID);
  if (gtmId && gtmReady && !analyticsHitSent) {
    pushDataLayerEvent('analytics_consent_granted', { page_path: pagePath });
    analyticsHitSent = true;
  }
}

function retrySendGa4PageView(): void {
  if (!analyticsConsentGranted || analyticsHitSent) return;
  sendGa4PageView();
}

function trySendAnalyticsHit(): void {
  sendGa4PageView();
}

function onGtagJsReady(): void {
  if (gtagJsReady) return;
  gtagJsReady = true;
  trySendAnalyticsHit();
  for (const delay of [150, 500, 1500]) {
    setTimeout(retrySendGa4PageView, delay);
  }
}

function onGtmContainerReady(): void {
  if (gtmReady) return;
  gtmReady = true;
  trySendAnalyticsHit();
}

function scheduleGtagReadyFallback(): void {
  for (const delay of [0, 50, 200, 500]) {
    setTimeout(() => {
      if (!gtagJsReady && document.querySelector(`script[${GTAG_SCRIPT_ATTR}]`)) {
        onGtagJsReady();
      }
    }, delay);
  }
}

export function syncGoogleConsent(analyticsGranted: boolean): void {
  if (typeof window === 'undefined' || !isGoogleTagsConfigured()) return;

  analyticsConsentGranted = analyticsGranted;
  analyticsHitSent = false;

  const state = analyticsGranted ? 'granted' : 'denied';
  gtag('consent', 'update', {
    ad_storage: state,
    ad_user_data: state,
    ad_personalization: state,
    analytics_storage: state,
  });
  gtag('set', 'ads_data_redaction', !analyticsGranted);

  if (analyticsGranted) {
    ensureGoogleTagsLoaded();
    trySendAnalyticsHit();
  }
}

function loadGtmContainer(gtmId: string): void {
  if (typeof document === 'undefined') return;
  if (document.querySelector(`script[${GTM_SCRIPT_ATTR}]`)) {
    onGtmContainerReady();
    return;
  }

  dataLayer().push({ 'gtm.start': Date.now(), event: 'gtm.js' });

  const script = document.createElement('script');
  script.async = true;
  script.setAttribute(GTM_SCRIPT_ATTR, 'true');
  script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(gtmId)}`;
  script.addEventListener('load', onGtmContainerReady, { once: true });
  document.head.appendChild(script);
  setTimeout(onGtmContainerReady, 500);
}

function loadGtagJs(measurementId: string): void {
  if (typeof document === 'undefined') return;
  if (document.querySelector(`script[${GTAG_SCRIPT_ATTR}]`)) {
    onGtagJsReady();
    return;
  }

  gtag('js', new Date());

  const script = document.createElement('script');
  script.async = true;
  script.setAttribute(GTAG_SCRIPT_ATTR, 'true');
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
  script.addEventListener('load', onGtagJsReady, { once: true });
  document.head.appendChild(script);
  scheduleGtagReadyFallback();
}

export function ensureGoogleTagsLoaded(): void {
  if (typeof window === 'undefined' || tagsLoaded) return;

  const gtmId = trimEnv(import.meta.env.VITE_GTM_ID);
  const measurementId = trimEnv(import.meta.env.VITE_GA_MEASUREMENT_ID);

  // GA4 directo es más fiable que GTM+gtag en paralelo.
  if (measurementId) {
    loadGtagJs(measurementId);
  } else if (gtmId) {
    loadGtmContainer(gtmId);
  } else {
    return;
  }

  tagsLoaded = true;
}

export function bootstrapGoogleTags(): void {
  if (!analyticsConsentGranted) return;
  ensureGoogleTagsLoaded();
}

export function applyGoogleAnalyticsConsent(analyticsGranted: boolean): void {
  if (!isGoogleTagsConfigured()) return;
  syncGoogleConsent(analyticsGranted);
}

export function trackGooglePageView(path: string): void {
  if (!isGoogleTagsConfigured() || !analyticsConsentGranted) return;

  const pagePath = path || '/';
  const measurementId = trimEnv(import.meta.env.VITE_GA_MEASUREMENT_ID);

  if (measurementId && gtagJsReady) {
    gtag('event', 'page_view', { page_path: pagePath });
    return;
  }

  pushDataLayerEvent('page_view', { page_path: pagePath });
}

export function trackGoogleDemoScheduled(): void {
  if (!ensureAnalyticsReady()) return;

  const sendTo = googleAdsDemoSendTo();
  const payload = { funnel: 'demo_request' };

  sendGtagEvent('demo_scheduled', payload, { dataLayerEvent: 'demo_scheduled' });

  // Evento recomendado por Google Ads (Reserva de cita / BOOK_APPOINTMENT).
  sendGtagEvent('conversion_event_book_appointment', payload, {
    dataLayerEvent: 'conversion_event_book_appointment',
  });

  if (sendTo) {
    sendGoogleAdsConversion(sendTo, { dataLayerEvent: 'ads_conversion' });
  }
}

/** Clic al WhatsApp de ventas (FAB / backup del formulario). */
export function trackGoogleWhatsAppClick(location: string): void {
  if (!ensureAnalyticsReady()) return;

  const payload = { funnel: 'whatsapp_sales', location };
  sendGtagEvent('whatsapp_click', payload, { dataLayerEvent: 'whatsapp_click' });

  const sendTo = googleAdsWhatsAppSendTo();
  if (sendTo) {
    sendGoogleAdsConversion(sendTo, { dataLayerEvent: 'ads_whatsapp_conversion' });
  }
}

export function resetGoogleTagsForTests(): void {
  tagsLoaded = false;
  analyticsConsentGranted = false;
  gtagJsReady = false;
  gtmReady = false;
  analyticsHitSent = false;
  if (typeof window !== 'undefined') {
    window.dataLayer = [];
  }
}
